import { Widget } from '../core/Widget.js';
import { ImSys } from '../core/ImSys.js';

/**
 * Общий виджет CAPTCHA.
 *
 * View объявляет только требование CAPTCHA маркером data-capt. Сервер выбирает
 * CaptchaSettings текущего сайта и добавляет на связанную кнопку:
 *  - data-captp="google" | "yandex"
 *  - data-captk="SITE_KEY"
 */
class CaptchaWidget extends Widget {
    constructor() {
        super(...arguments);
        this._provider = null;
    }
    /** Инициализация: разрешаем настройки кнопки и грузим нужный SDK. */
    _create() {
        this._provider = this._findProvider();
        if (!this._provider) {
            console.error("[CaptchaWidget] Провайдер не разрешён серверным профилем CAPTCHA");
            return;
        }
        window.captchaCallback = () => {
            this._render();
        };
        if (this._provider === "google") {
            this._ensureGoogleScript();
        }
        else {
            this._ensureYandexScript();
        }
    }
    /** Возвращает токен, если он уже установлен, иначе null. */
    getToken() {
        return (this.element.querySelector('input[data-key="captchaToken"]')?.value ||
            null);
    }
    /** Находит связанную кнопку: сначала в пределах ближайшей формы, затем глобально. */
    _resolveRelatedButton() {
        const inForm = this.element
            .closest("form")
            ?.querySelector(`button[data-capt]`);
        if (inForm)
            return inForm;
        return document.querySelector(`button[data-capt]`);
    }
    /** Возвращает клиентский provider, уже разрешённый сервером из CaptchaSettings. */
    _findProvider() {
        const provider = this._resolveRelatedButton()?.dataset.captp?.toLowerCase();
        return provider === "google" || provider === "yandex"
            ? provider
            : null;
    }
    /** Достаёт публичный site key с кнопки. */
    _findSiteKey() {
        return this._resolveRelatedButton()?.dataset.captk ?? null;
    }
    /** Создаёт скрытый input для унифицированного server token. */
    _ensureTokenInput() {
        let input = this.element.querySelector('input[data-key="captchaToken"]');
        if (!input) {
            input = document.createElement("input");
            input.type = "hidden";
            input.name = "captchaToken";
            input.dataset.key = "captchaToken";
            this.element.prepend(input);
        }
        return input;
    }
    _clearToken() {
        const input = this.element.querySelector('input[data-key="captchaToken"]');
        if (input)
            input.value = "";
    }
    _ensureGoogleScript() {
        if (window.grecaptcha) {
            this._render();
            return;
        }
        if (!document.querySelector('script[data-script="recaptcha"]')) {
            const script = document.createElement("script");
            script.dataset.script = "recaptcha";
            script.src = `https://www.google.com/recaptcha/enterprise.js?onload=captchaCallback&render=explicit&hl=${ImSys.uiLang}`;
            script.async = true;
            document.body.appendChild(script);
        }
    }
    _ensureYandexScript() {
        if (window.smartCaptcha) {
            this._render();
            return;
        }
        if (!document.querySelector('script[data-script="yacaptcha"]')) {
            const script = document.createElement("script");
            script.dataset.script = "yacaptcha";
            script.src = "https://smartcaptcha.yandexcloud.net/captcha.js?render=onload&onload=captchaCallback";
            script.async = true;
            document.body.appendChild(script);
        }
    }
    _render() {
        if (this._provider === "google") {
            try {
                this._renderGoogle();
            }
            catch (error) {
                console.error(error);
            }
        }
        else if (this._provider === "yandex") {
            try {
                this._renderYandex();
            }
            catch (error) {
                console.error(error);
            }
        }
    }
    /** Рендер Google reCAPTCHA Enterprise в общем контейнере. */
    _renderGoogle() {
        if (!window.grecaptcha)
            return;
        const siteKey = this._findSiteKey();
        if (!siteKey) {
            console.error("[CaptchaWidget] sitekey не найден (data-captk на кнопке)");
            return;
        }
        window.grecaptcha.enterprise.render(this.element, {
            sitekey: siteKey,
            callback: (token) => {
                this._ensureTokenInput().value = token;
                this.element.classList.remove("input-validation-error");
            },
            "expired-callback": () => {
                this._clearToken();
                this.element.classList.add("input-validation-error");
            },
            "error-callback": () => {
                this._clearToken();
                this.element.classList.add("input-validation-error");
            }
        });
    }
    /** Рендер Yandex SmartCaptcha в общем контейнере. */
    _renderYandex() {
        if (!window.smartCaptcha)
            return;
        const siteKey = this._findSiteKey();
        if (!siteKey) {
            console.error("[CaptchaWidget] sitekey не найден (data-captk на кнопке)");
            return;
        }
        const widgetId = window.smartCaptcha.render(this.element, {
            sitekey: siteKey,
            hl: ImSys.uiLang
        });
        this._ensureTokenInput();
        this._yaUnsubSuccess = window.smartCaptcha.subscribe(widgetId, "success", (token) => {
            this._ensureTokenInput().value = token || window.smartCaptcha?.getResponse?.(widgetId) || "";
            this.element.classList.remove("input-validation-error");
        });
        this._yaUnsubExpired = window.smartCaptcha.subscribe(widgetId, "token-expired", () => {
            this._clearToken();
            this.element.classList.add("input-validation-error");
        });
    }
    _onDestroy() {
        if (this._yaUnsubSuccess)
            this._yaUnsubSuccess();
        if (this._yaUnsubExpired)
            this._yaUnsubExpired();
        this._yaUnsubSuccess = null;
        this._yaUnsubExpired = null;
    }
}
/** Имя виджета */
CaptchaWidget._alias = "Captcha";

export { CaptchaWidget };
